import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { SlidersHorizontal, Clock, Loader2 } from 'lucide-react';
import {
  CASE_STATUSES,
  CASE_PRIORITIES,
  CASE_CATEGORIES,
  DEAL_STAGES,
  B2B_CATEGORY,
  priorityMeta,
  inputClass,
} from './caseConstants';

// The case's own state: where it is in the queue, how urgent it is, who owns
// it, and when it should resurface. The Deal Stage only applies to a corporate
// event case, so it stays hidden for everything else.
export default function CaseMetaControls({ caseData, admins = [], onDone }) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [snooze, setSnooze] = useState(toLocalInput(caseData.snooze_until));

  const patch = async (fields, key) => {
    setBusy(key);
    setError('');
    try {
      const res = await base44.functions.invoke('caseUpdate', { caseId: caseData.id, ...fields });
      if (res.data?.error) throw new Error(res.data.error);
      if (onDone) onDone();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not update the case.');
    }
    setBusy('');
  };

  const priority = priorityMeta(caseData.priority);
  const isB2B = caseData.category === B2B_CATEGORY;

  return (
    <div className="border border-white/5 bg-[#111115]">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-3.5 h-3.5 text-primary" />
          <p className="text-white text-[10px] font-bold tracking-widest">CASE CONTROLS</p>
        </div>
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />}
      </div>

      <div className="px-4 py-3 space-y-3">
        <Row label="STATUS">
          <select
            value={caseData.status || 'open'}
            onChange={(e) => patch({ status: e.target.value }, 'status')}
            className={inputClass}
          >
            {CASE_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </Row>

        <Row label="PRIORITY">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full shrink-0 ${priority.dot}`} />
            <select
              value={caseData.priority || 'medium'}
              onChange={(e) => patch({ priority: e.target.value }, 'priority')}
              className={`${inputClass} ${priority.text}`}
            >
              {CASE_PRIORITIES.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
        </Row>

        <Row label="ASSIGNED OWNER">
          <select
            value={caseData.assigned_to || ''}
            onChange={(e) => patch({ assigned_to: e.target.value }, 'owner')}
            className={inputClass}
          >
            <option value="">Unassigned</option>
            {admins.map((a) => (
              <option key={a.email} value={a.email}>{a.name}</option>
            ))}
          </select>
        </Row>

        <Row label="CATEGORY">
          <select
            value={caseData.category || 'general_support'}
            onChange={(e) => patch({ category: e.target.value }, 'category')}
            className={inputClass}
          >
            {CASE_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </Row>

        {isB2B && (
          <Row label="DEAL STAGE">
            <select
              value={caseData.deal_stage || ''}
              onChange={(e) => patch({ deal_stage: e.target.value }, 'deal')}
              className={inputClass}
            >
              <option value="">Not set</option>
              {DEAL_STAGES.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </Row>
        )}

        <div className="pt-2 border-t border-white/5">
          <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5 flex items-center gap-1.5">
            <Clock className="w-3 h-3" /> SNOOZE CASE
          </p>
          <input
            type="datetime-local"
            value={snooze}
            onChange={(e) => setSnooze(e.target.value)}
            className={inputClass}
          />
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => patch({ snooze_until: toIso(snooze) }, 'snooze')}
              disabled={!snooze}
              className="flex-1 border border-primary/40 hover:bg-primary/10 text-primary text-[10px] font-bold tracking-widest px-3 py-1.5 transition-colors disabled:opacity-40"
            >
              SNOOZE
            </button>
            {caseData.snooze_until && (
              <button
                onClick={() => {
                  setSnooze('');
                  patch({ snooze_until: '' }, 'snooze');
                }}
                className="flex-1 border border-white/10 hover:border-white/30 text-white/50 hover:text-white text-[10px] font-bold tracking-widest px-3 py-1.5 transition-colors"
              >
                CLEAR
              </button>
            )}
          </div>
          {caseData.snooze_until && (
            <p className="text-white/30 text-[10px] mt-2">
              Hidden from the queue until {new Date(caseData.snooze_until).toLocaleString('en-GB')}
            </p>
          )}
        </div>

        {error && <p className="text-red-400 text-[11px]">{error}</p>}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">{label}</p>
      {children}
    </div>
  );
}

function toIso(local) {
  return local ? new Date(local).toISOString() : '';
}

function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}